import ApiIcon from "@mui/icons-material/Api";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import BusinessIcon from "@mui/icons-material/Business";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CalendarViewDayIcon from "@mui/icons-material/CalendarViewDay";
import CalendarViewMonthIcon from "@mui/icons-material/CalendarViewMonth";
import CampaignIcon from "@mui/icons-material/Campaign";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import DashboardIcon from "@mui/icons-material/Dashboard";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import FavoriteIcon from "@mui/icons-material/Favorite";
import GridViewIcon from "@mui/icons-material/GridView";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import HomeIcon from "@mui/icons-material/Home";
import InsightsIcon from "@mui/icons-material/Insights";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import PsychologyIcon from "@mui/icons-material/Psychology";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import SettingsIcon from "@mui/icons-material/Settings";
import ShieldIcon from "@mui/icons-material/Shield";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TuneIcon from "@mui/icons-material/Tune";
import VerifiedIcon from "@mui/icons-material/Verified";
import VpnKeyIcon from "@mui/icons-material/VpnKey";

export type NavItem = {
  key: string;
  icon: React.ReactNode;
  to: string;
  permission?: string;
  /** Hide for accounts with no tenant_id (GOD): the page has nothing to scope to. */
  requiresTenant?: boolean;
  /** Accent color used by the homepage tile. */
  color?: string;
};

export type NavSection = { id: string; items: NavItem[] };

export const NAV_SECTIONS: NavSection[] = [
  {
    id: "home",
    items: [
      { key: "home", icon: <HomeIcon />, to: "/", color: "#1976d2" },
      { key: "myHome", icon: <HomeIcon />, to: "/my-home", color: "#1565c0" },
      { key: "dashboard", icon: <DashboardIcon />, to: "/dashboard", color: "#0d47a1" },
    ],
  },
  {
    id: "planning",
    items: [
      { key: "planning", icon: <CalendarMonthIcon />, to: "/planning", color: "#2e7d32" },
      { key: "myPlanning", icon: <CalendarViewMonthIcon />, to: "/my-planning", color: "#388e3c" },
      { key: "roster", icon: <CalendarViewDayIcon />, to: "/roster", permission: "planning:read", color: "#43a047" },
      { key: "heatmap", icon: <GridViewIcon />, to: "/heatmap", permission: "planning:read", color: "#558b2f" },
      { key: "exchange", icon: <SwapHorizIcon />, to: "/exchange", color: "#0277bd" },
      { key: "desiderata", icon: <FavoriteIcon />, to: "/desiderata", color: "#c2185b" },
      { key: "logbook", icon: <MenuBookIcon />, to: "/logbook", color: "#5d4037" },
      { key: "myActivity", icon: <ReceiptLongIcon />, to: "/my-activity", color: "#00796b" },
    ],
  },
  {
    id: "people",
    items: [
      { key: "organizations", icon: <BusinessIcon />, to: "/organizations", color: "#6a1b9a" },
      { key: "practitioners", icon: <LocalHospitalIcon />, to: "/practitioners", color: "#8e24aa" },
      { key: "directory", icon: <ContactPhoneIcon />, to: "/directory", color: "#9c27b0" },
    ],
  },
  {
    id: "pilotage",
    items: [
      { key: "workload", icon: <QueryStatsIcon />, to: "/workload", permission: "practitioner:read", color: "#ef6c00" },
      { key: "burnout", icon: <MonitorHeartIcon />, to: "/burnout", permission: "burnout:read_all", color: "#d84315" },
      { key: "forecast", icon: <InsightsIcon />, to: "/forecast", permission: "planning:edit", color: "#f57c00" },
      { key: "orgHealth", icon: <HealthAndSafetyIcon />, to: "/org-health", permission: "planning:edit", color: "#ff8f00" },
      { key: "compliance", icon: <VerifiedIcon />, to: "/compliance", permission: "document:read_all", color: "#ef6c00" },
    ],
  },
  {
    id: "admin",
    items: [
      { key: "users", icon: <ManageAccountsIcon />, to: "/admin/users", permission: "user:read", color: "#37474f" },
      { key: "applications", icon: <ContactMailIcon />, to: "/admin/applications", permission: "user:create", color: "#455a64" },
      { key: "timesheets", icon: <FactCheckIcon />, to: "/admin/timesheets", permission: "timesheet:read_all", color: "#00695c" },
      { key: "unavailabilities", icon: <BeachAccessIcon />, to: "/admin/unavailabilities", permission: "unavailability:review", color: "#546e7a" },
      { key: "skills", icon: <PsychologyIcon />, to: "/admin/skills", permission: "skill:write", color: "#37474f" },
      { key: "functionalRoles", icon: <HeadsetMicIcon />, to: "/admin/functional-roles", permission: "skill:write", color: "#3f51b5" },
      { key: "shiftPatterns", icon: <EventRepeatIcon />, to: "/admin/shift-patterns", permission: "planning:edit", color: "#455a64" },
      { key: "announcements", icon: <CampaignIcon />, to: "/admin/announcements", permission: "announcement:write", color: "#546e7a" },
      { key: "apiTokens", icon: <VpnKeyIcon />, to: "/admin/api-tokens", permission: "api_token:manage", color: "#37474f" },
      { key: "sso", icon: <VpnKeyIcon />, to: "/admin/sso", permission: "sso:manage", color: "#4527a0" },
      { key: "tenantSettings", icon: <TuneIcon />, to: "/admin/tenant-settings", permission: "tenant:update", requiresTenant: true, color: "#455a64" },
      { key: "tenants", icon: <BusinessCenterIcon />, to: "/admin/tenants", permission: "__god__", color: "#263238" },
      { key: "smtp", icon: <ContactMailIcon />, to: "/admin/smtp", permission: "__god__", color: "#263238" },
      { key: "audit", icon: <ShieldIcon />, to: "/audit", permission: "audit:read", color: "#37474f" },
      { key: "settings", icon: <SettingsIcon />, to: "/settings", color: "#546e7a" },
      { key: "profile", icon: <AssignmentIndIcon />, to: "/profile", color: "#455a64" },
      { key: "apiDocs", icon: <ApiIcon />, to: "/api/v1/docs", permission: "__god__", color: "#263238" },
    ],
  },
];

export const SECTION_IDS = NAV_SECTIONS.map((s) => s.id);
